import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Coffee, StopCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const fmtElapsed = (startIso) => {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(startIso + (startIso.endsWith('Z') ? '' : 'Z')).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const BreakTracker = ({ token, clockedIn }) => {
  const { toast } = useToast();
  const [activeBreak, setActiveBreak] = useState(null);
  const [today, setToday] = useState({ count: 0, paid_minutes: 0, unpaid_minutes: 0 });
  const [choosing, setChoosing] = useState(false);
  const [breakType, setBreakType] = useState('unpaid');
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/time/break/current`, auth);
      setActiveBreak(data.active_break);
      setToday(data.today || { count: 0, paid_minutes: 0, unpaid_minutes: 0 });
    } catch (e) { /* silent */ }
  }, [token]); // eslint-disable-line

  useEffect(() => { if (token && clockedIn) refresh(); }, [token, clockedIn, refresh]);
  useEffect(() => {
    if (!activeBreak) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [activeBreak]);

  if (!clockedIn) return null;

  const startBreak = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/time/break/start`, { break_type: breakType }, auth);
      setChoosing(false);
      await refresh();
      toast({ title: `${breakType === 'paid' ? 'Paid' : 'Unpaid'} break started` });
    } catch (e) {
      toast({ title: 'Could not start break', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const endBreak = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time/break/end`, {}, auth);
      await refresh();
      toast({ title: 'Break ended', description: `${Math.round(data.duration_minutes)} minute${Math.round(data.duration_minutes) === 1 ? '' : 's'}` });
    } catch (e) {
      toast({ title: 'Could not end break', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card className="shadow-xl mb-6 border-2 border-[#2D3748]" data-testid="break-tracker-card">
      <CardContent className="p-6 space-y-4">
        {activeBreak ? (
          <div className="text-center space-y-3">
            <p className="text-sm text-amber-400 font-semibold uppercase tracking-wide">
              On {activeBreak.break_type === 'paid' ? 'Paid' : 'Unpaid'} Break
            </p>
            <p className="text-4xl font-bold text-white tabular-nums" data-testid="break-timer">
              {fmtElapsed(typeof activeBreak.break_start === 'string' ? activeBreak.break_start : new Date(activeBreak.break_start).toISOString())}
            </p>
            <Button
              onClick={endBreak}
              disabled={busy}
              className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              data-testid="end-break-btn"
            >
              <StopCircle className="w-5 h-5 mr-2" />
              End Break
            </Button>
          </div>
        ) : choosing ? (
          <div className="space-y-3" data-testid="break-type-selector">
            <p className="text-sm text-slate-400 font-semibold">What kind of break?</p>
            <div className="grid grid-cols-2 gap-3">
              {['paid', 'unpaid'].map((t) => (
                <button
                  key={t}
                  onClick={() => setBreakType(t)}
                  className={`p-3 rounded-xl border text-sm font-semibold transition-colors ${
                    breakType === t
                      ? 'border-[#00C896] bg-[#0F1923] text-[#00C896]'
                      : 'border-[#2D3748] bg-[#0F1923] text-slate-400'
                  }`}
                  data-testid={`break-type-${t}`}
                >
                  {t === 'paid' ? 'Paid break' : 'Unpaid break'}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setChoosing(false)} data-testid="break-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={startBreak}
                disabled={busy}
                className="flex-1 bg-[#00C896] hover:bg-emerald-600 text-white font-semibold"
                data-testid="break-start-confirm-btn"
              >
                Start
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => setChoosing(true)}
            className="w-full h-12 bg-[#0F1923] hover:bg-[#1A2433] border border-[#2D3748] text-white font-semibold"
            data-testid="start-break-btn"
          >
            <Coffee className="w-5 h-5 mr-2 text-amber-400" />
            Start Break
          </Button>
        )}
        {today.count > 0 && (
          <p className="text-center text-xs text-slate-500" data-testid="break-today-summary">
            {today.count} break{today.count === 1 ? '' : 's'} | {Math.round(today.unpaid_minutes)} min unpaid | {Math.round(today.paid_minutes)} min paid
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakTracker;
